import React, { useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  ButtonGroup,
  TablePagination
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import api from "../../services/api";
import * as XLSX from "xlsx";
import SafeReactApexChart from "../Dashboard/SafeReactApexChart";
import { sanitizeApexOptions } from "../Dashboard/sanitizeApexOptions";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  searchBox: {
    marginBottom: theme.spacing(3)
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 600
  },
  table: {
    minWidth: 650
  },
  refreshButton: {
    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    marginLeft: theme.spacing(2),
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)"
    }
  },
  exportButton: {
    background: "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    marginTop: theme.spacing(2),
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)"
    }
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  statsBox: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  statCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.grey[100],
    minWidth: 150
  },
  sentChip: {
    backgroundColor: "#4caf50",
    color: "white"
  },
  notSentChip: {
    backgroundColor: "#f44336",
    color: "white"
  },
  checkIcon: {
    color: "#4caf50"
  },
  cancelIcon: {
    color: "#f44336"
  },
  chartContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  chartPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    padding: theme.spacing(2)
  }
}));

const BirthdayReport = () => {
  const classes = useStyles();
  const [searchType, setSearchType] = useState("date"); // "date" ou "month"
  const [reportDate, setReportDate] = useState("");
  const [reportMonth, setReportMonth] = useState("");
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    notSent: 0
  });

  // Removido useEffect - não carregar automaticamente ao montar

  const loadReport = async (date = null, month = null) => {
    setLoading(true);
    setHasLoaded(true);
    
    try {
      const params = {};
      if (month) {
        params.month = month;
      } else if (date) {
        params.date = date;
      }
      
      const { data } = await api.get("/birthdays/send-report", { params });
      
      setReport(data.data || []);
      setStats({
        total: data.count || 0,
        sent: data.sentCount || 0,
        notSent: data.notSentCount || 0
      });
      // Resetar paginação quando novos dados são carregados
      setPage(0);
      
      if (data.data && data.data.length === 0) {
        const message = month 
          ? "Nenhum aniversariante encontrado para este mês"
          : "Nenhum aniversariante encontrado para esta data";
        toast.info(message);
      }
    } catch (error) {
      console.error("Erro ao carregar relatório:", error);
      toast.error("Erro ao carregar relatório de envios");
      setReport([]);
      setStats({ total: 0, sent: 0, notSent: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (searchType === "month" && reportMonth) {
      loadReport(null, reportMonth);
    } else if (searchType === "date" && reportDate) {
      // Converter data do formato YYYY-MM-DD para DD/MM/YYYY
      const [year, month, day] = reportDate.split('-');
      const formattedDate = `${day}/${month}/${year}`;
      loadReport(formattedDate);
    } else {
      loadReport();
    }
  };

  const handleDateChange = (e) => {
    setReportDate(e.target.value);
    if (e.target.value) {
      // Converter data do formato YYYY-MM-DD para DD/MM/YYYY
      const [year, month, day] = e.target.value.split('-');
      const formattedDate = `${day}/${month}/${year}`;
      loadReport(formattedDate);
    } else {
      loadReport();
    }
  };

  const handleMonthChange = (e) => {
    setReportMonth(e.target.value);
    if (e.target.value) {
      loadReport(null, e.target.value);
    } else {
      loadReport();
    }
  };

  const handleExportToExcel = () => {
    if (report.length === 0) {
      toast.error("Não há dados para exportar");
      return;
    }

    try {
      // Preparar dados para exportação
      const exportData = report.map((item, index) => ({
        "#": index + 1,
        "Nome": item.contactName || "",
        "Número": item.contactNumber || "",
        "Email": item.contactEmail || "",
        "Data de Nascimento": item.birthDate || "",
        "Idade": item.age || "N/A",
        "Mensagem Enviada": item.messageSent ? "Sim" : "Não",
        "Enviado em": item.sentAt || "-"
      }));

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Ajustar largura das colunas
      const colWidths = [
        { wch: 5 },   // #
        { wch: 30 },  // Nome
        { wch: 20 },  // Número
        { wch: 30 },  // Email
        { wch: 18 },  // Data de Nascimento
        { wch: 10 },  // Idade
        { wch: 15 },  // Mensagem Enviada
        { wch: 20 }   // Enviado em
      ];
      ws['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Envios");

      // Gerar nome do arquivo
      const dateStr = searchType === "month" 
        ? (reportMonth || new Date().toISOString().slice(0, 7))
        : (reportDate || new Date().toISOString().split('T')[0]);
      const fileName = `Relatorio_Envios_Aniversario_${dateStr.replace(/-/g, '_')}.xlsx`;

      // Fazer download do arquivo
      XLSX.writeFile(wb, fileName);

      toast.success(`Arquivo "${fileName}" exportado com sucesso!`);
    } catch (err) {
      console.error("Erro ao exportar para Excel:", err);
      toast.error("Erro ao exportar para Excel");
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return "";
    
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Preparar dados para gráficos
  const getChartData = () => {
    if (report.length === 0) return null;

    // Gráfico de pizza: Enviados vs Não Enviados
    const pieChartData = {
      series: [stats.sent, stats.notSent],
      options: {
        chart: {
          type: 'donut',
          height: 350
        },
        labels: ['Enviados', 'Não Enviados'],
        colors: ['#4caf50', '#f44336'],
        legend: {
          position: 'bottom'
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => stats.total.toString()
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val.toFixed(1)}%`
        },
        title: {
          text: 'Status de Envios',
          align: 'center',
          style: {
            fontSize: '16px',
            fontWeight: 600
          }
        }
      }
    };

    // Gráfico de barras: Distribuição por dia do mês (apenas quando buscar por mês)
    let barChartData = null;
    if (searchType === "month" && reportMonth) {
      // Agrupar por dia do mês
      const dayStats = {};
      report.forEach(item => {
        const day = parseInt(item.birthDate.split('/')[0]);
        if (!dayStats[day]) {
          dayStats[day] = { total: 0, sent: 0, notSent: 0 };
        }
        dayStats[day].total++;
        if (item.messageSent) {
          dayStats[day].sent++;
        } else {
          dayStats[day].notSent++;
        }
      });

      const days = Object.keys(dayStats).sort((a, b) => parseInt(a) - parseInt(b));
      const sentData = days.map(day => dayStats[day].sent);
      const notSentData = days.map(day => dayStats[day].notSent);

      barChartData = {
        series: [
          {
            name: 'Enviados',
            data: sentData
          },
          {
            name: 'Não Enviados',
            data: notSentData
          }
        ],
        options: {
          chart: {
            type: 'bar',
            height: 350,
            stacked: true,
            toolbar: {
              show: true
            }
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '55%',
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: '13px',
                    fontWeight: 600
                  }
                }
              }
            }
          },
          dataLabels: {
            enabled: false
          },
          xaxis: {
            categories: days.map(day => `Dia ${day}`),
            title: {
              text: 'Dia do Mês'
            }
          },
          yaxis: {
            title: {
              text: 'Quantidade de Contatos'
            }
          },
          colors: ['#4caf50', '#f44336'],
          legend: {
            position: 'top',
            horizontalAlign: 'right'
          },
          title: {
            text: 'Distribuição de Envios por Dia do Mês',
            align: 'center',
            style: {
              fontSize: '16px',
              fontWeight: 600
            }
          },
          tooltip: {
            y: {
              formatter: (val) => `${val} contato(s)`
            }
          }
        }
      };
    }

    return { pieChartData, barChartData };
  };

  const chartData = getChartData();
  const paginatedReport = report.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className={classes.root}>
      <Typography variant="h5" gutterBottom style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CheckIcon />
        Relatório de Envios
      </Typography>

      <Paper className={classes.paper}>
        <Box className={classes.searchBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Tipo de busca:
                </Typography>
                <ButtonGroup
                  variant="contained"
                  color="primary"
                  aria-label="tipo de busca"
                >
                  <Button
                    variant={searchType === "date" ? "contained" : "outlined"}
                    onClick={() => {
                      setSearchType("date");
                      setReportDate("");
                      setReportMonth("");
                      setReport([]);
                      setHasLoaded(false);
                      setStats({ total: 0, sent: 0, notSent: 0 });
                      setLoading(false);
                      // Não carregar automaticamente - usuário deve escolher a data ou clicar em Atualizar
                    }}
                    style={{
                      backgroundColor: searchType === "date" ? undefined : "transparent",
                      color: searchType === "date" ? undefined : "inherit"
                    }}
                  >
                    Por Data
                  </Button>
                  <Button
                    variant={searchType === "month" ? "contained" : "outlined"}
                    onClick={() => {
                      setSearchType("month");
                      setReportDate("");
                      setReportMonth("");
                      setReport([]);
                      setHasLoaded(false);
                      setStats({ total: 0, sent: 0, notSent: 0 });
                      setLoading(false);
                      // Não carregar automaticamente quando mudar para "Por Mês"
                    }}
                    style={{
                      backgroundColor: searchType === "month" ? undefined : "transparent",
                      color: searchType === "month" ? undefined : "inherit"
                    }}
                  >
                    Por Mês
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={searchType === "date" ? 4 : 6}>
              {searchType === "date" ? (
                <TextField
                  type="date"
                  label="Data do Relatório"
                  value={reportDate}
                  onChange={handleDateChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Deixe em branco para ver o relatório de hoje"
                />
              ) : (
                <TextField
                  type="month"
                  label="Mês do Relatório"
                  value={reportMonth}
                  onChange={handleMonthChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Selecione o mês para ver o relatório completo"
                />
              )}
            </Grid>
            <Grid item xs={12} md={searchType === "date" ? 8 : 6}>
              <Button
                variant="contained"
                className={classes.refreshButton}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? "Carregando..." : "Atualizar"}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {hasLoaded && (
          <>
            {report.length > 0 && (
              <>
                <Box className={classes.statsBox}>
                  <Box className={classes.statCard}>
                    <Typography variant="caption" color="textSecondary">
                      Total de Aniversariantes
                    </Typography>
                    <Typography variant="h5">{stats.total}</Typography>
                  </Box>
                  <Box className={classes.statCard}>
                    <Typography variant="caption" color="textSecondary">
                      Mensagens Enviadas
                    </Typography>
                    <Typography variant="h5" style={{ color: "#4caf50" }}>
                      {stats.sent}
                    </Typography>
                  </Box>
                  <Box className={classes.statCard}>
                    <Typography variant="caption" color="textSecondary">
                      Mensagens Não Enviadas
                    </Typography>
                    <Typography variant="h5" style={{ color: "#f44336" }}>
                      {stats.notSent}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <Button
                    variant="contained"
                    className={classes.exportButton}
                    startIcon={<ExportIcon />}
                    onClick={handleExportToExcel}
                  >
                    Exportar para Excel
                  </Button>
                </Box>

                {/* Gráficos */}
                {chartData && (
                  <Grid container spacing={2} className={classes.chartContainer}>
                    {/* Gráfico de Pizza - Status de Envios */}
                    <Grid item xs={12} md={searchType === "month" ? 6 : 12}>
                      <Paper className={classes.chartPaper}>
                        {chartData.pieChartData && (
                          <SafeReactApexChart
                            options={sanitizeApexOptions(chartData.pieChartData.options)}
                            series={chartData.pieChartData.series}
                            type="donut"
                            height={350}
                          />
                        )}
                      </Paper>
                    </Grid>

                    {/* Gráfico de Barras - Distribuição por Dia (apenas para busca por mês) */}
                    {searchType === "month" && chartData.barChartData && (
                      <Grid item xs={12} md={6}>
                        <Paper className={classes.chartPaper}>
                          <SafeReactApexChart
                            options={sanitizeApexOptions(chartData.barChartData.options)}
                            series={chartData.barChartData.series}
                            type="bar"
                            height={350}
                          />
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                )}

                <TableContainer className={classes.tableContainer}>
                  <Table className={classes.table} stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Enviado</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell>Número</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Data de Nascimento</TableCell>
                        <TableCell>Idade</TableCell>
                        <TableCell>Enviado em</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedReport.map((item) => (
                        <TableRow key={item.contactId} hover>
                          <TableCell>
                            {item.messageSent ? (
                              <CheckIcon className={classes.checkIcon} />
                            ) : (
                              <CancelIcon className={classes.cancelIcon} />
                            )}
                          </TableCell>
                          <TableCell>{item.contactName}</TableCell>
                          <TableCell>{formatPhoneNumber(item.contactNumber)}</TableCell>
                          <TableCell>{item.contactEmail || "-"}</TableCell>
                          <TableCell>{item.birthDate}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.age ? `${item.age} ano(s)` : "N/A"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{item.sentAt || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Paginação */}
                <TablePagination
                  component="div"
                  count={report.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                />
              </>
            )}

            {report.length === 0 && !loading && (
              <Box className={classes.emptyState}>
                <CheckIcon style={{ fontSize: 64, opacity: 0.3, marginBottom: 16 }} />
                <Typography variant="h6" color="textSecondary">
                  {searchType === "date" 
                    ? "Nenhum aniversariante encontrado para esta data"
                    : "Nenhum aniversariante encontrado para este mês"}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {searchType === "date" 
                    ? "Tente selecionar outra data ou verifique se há contatos com data de nascimento cadastrada"
                    : "Tente selecionar outro mês ou verifique se há contatos com data de nascimento cadastrada"}
                </Typography>
              </Box>
            )}
          </>
        )}

        {!hasLoaded && searchType === "date" && !reportDate && (
          <Box className={classes.emptyState}>
            <CheckIcon style={{ fontSize: 64, opacity: 0.3, marginBottom: 16 }} />
            <Typography variant="h6" color="textSecondary">
              Selecione uma data para ver o relatório de envios
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Escolha a data no campo acima ou deixe em branco e clique em "Atualizar" para ver o relatório de hoje
            </Typography>
          </Box>
        )}

        {!hasLoaded && searchType === "month" && !reportMonth && (
          <Box className={classes.emptyState}>
            <CheckIcon style={{ fontSize: 64, opacity: 0.3, marginBottom: 16 }} />
            <Typography variant="h6" color="textSecondary">
              Selecione um mês para ver o relatório de envios
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Escolha o mês no campo acima para visualizar quais contatos receberam mensagens de aniversário
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BirthdayReport;
